/**
 * Confidence Indicator Component System
 *
 * A reusable component for displaying data confidence/quality scores
 * throughout the application. Uses percentage-based confidence scores
 * with color coding to indicate reliability.
 *
 * Confidence Levels:
 * - Verified (90-100%): Multiple authoritative sources confirm
 * - High (70-89%): Single authoritative or multiple quality sources
 * - Moderate (50-69%): Inferred from secondary sources
 * - Low (25-49%): Single unverified mention
 * - Unverified (0-24%): No source attribution
 */

import type { DataQuality } from '../../types';

// Confidence level configuration
export interface ConfidenceLevel {
  label: string;
  minPercent: number;
  maxPercent: number;
  color: string;        // Tailwind text color
  bgColor: string;      // Tailwind background color
  barColor: string;     // Tailwind bar fill color
  ringColor: string;    // SVG hex color for ring
  description: string;
}

export const CONFIDENCE_LEVELS: ConfidenceLevel[] = [
  {
    label: 'Verified',
    minPercent: 90,
    maxPercent: 100,
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    barColor: 'bg-green-500',
    ringColor: '#10B981',
    description: 'Multiple authoritative sources confirm this data',
  },
  {
    label: 'High',
    minPercent: 70,
    maxPercent: 89,
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    barColor: 'bg-blue-500',
    ringColor: '#3B82F6',
    description: 'Single authoritative or multiple quality sources',
  },
  {
    label: 'Moderate',
    minPercent: 50,
    maxPercent: 69,
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
    barColor: 'bg-yellow-500',
    ringColor: '#F59E0B',
    description: 'Inferred from secondary sources',
  },
  {
    label: 'Low',
    minPercent: 25,
    maxPercent: 49,
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    barColor: 'bg-orange-500',
    ringColor: '#F97316',
    description: 'Single unverified mention',
  },
  {
    label: 'Unverified',
    minPercent: 0,
    maxPercent: 24,
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    barColor: 'bg-red-500',
    ringColor: '#EF4444',
    description: 'No source attribution available',
  },
];

// Map DataQuality enum to confidence percentage
export const DATA_QUALITY_TO_CONFIDENCE: Record<DataQuality, number> = {
  excellent: 95,
  good: 78,
  fair: 55,
  poor: 30,
};

// Get confidence level config for a percentage
export function getConfidenceLevel(percent: number): ConfidenceLevel {
  for (const level of CONFIDENCE_LEVELS) {
    if (percent >= level.minPercent) {
      return level;
    }
  }
  // Fallback to last level (low confidence) - CONFIDENCE_LEVELS is always populated
  return CONFIDENCE_LEVELS[CONFIDENCE_LEVELS.length - 1]!;
}

// Convert DataQuality to percentage
export function dataQualityToPercent(quality: DataQuality): number {
  return DATA_QUALITY_TO_CONFIDENCE[quality] ?? 50;
}

// Props for all confidence indicator variants
interface BaseConfidenceProps {
  /** Confidence as 0-100 percentage */
  confidence?: number;
  /** DataQuality enum (converted to confidence if provided) */
  dataQuality?: DataQuality;
  /** Show percentage value */
  showPercent?: boolean;
  /** Show label (Verified, High, etc.) */
  showLabel?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * ConfidenceBadge - Compact inline badge for tables and cards
 *
 * Replaces star ratings with a percentage + color indicator
 */
export function ConfidenceBadge({
  confidence,
  dataQuality,
  showPercent = true,
  showLabel = false,
  className = '',
}: BaseConfidenceProps) {
  const percent = confidence ?? (dataQuality ? dataQualityToPercent(dataQuality) : 0);
  const level = getConfidenceLevel(percent);

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${level.bgColor} ${level.color} ${className}`}
      title={`${level.label}: ${level.description}`}
    >
      {/* Mini progress bar */}
      <span className="relative w-8 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <span
          className={`absolute left-0 top-0 h-full rounded-full ${level.barColor}`}
          style={{ width: `${percent}%` }}
        />
      </span>
      {showPercent && <span>{Math.round(percent)}%</span>}
      {showLabel && <span>{level.label}</span>}
    </span>
  );
}

/**
 * ConfidenceBar - Larger progress bar for detail views
 *
 * Shows percentage with full-width progress bar
 */
export function ConfidenceBar({
  confidence,
  dataQuality,
  showPercent = true,
  showLabel = true,
  className = '',
}: BaseConfidenceProps & { height?: 'sm' | 'md' | 'lg' }) {
  const percent = confidence ?? (dataQuality ? dataQualityToPercent(dataQuality) : 0);
  const level = getConfidenceLevel(percent);

  return (
    <div className={`space-y-1 ${className}`}>
      <div className="flex items-center justify-between text-sm">
        {showLabel && (
          <span className={`font-medium ${level.color}`}>{level.label}</span>
        )}
        {showPercent && (
          <span className="text-gray-600">{Math.round(percent)}%</span>
        )}
      </div>
      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${level.barColor}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}

/**
 * ConfidenceRing - Circular progress indicator for dashboards
 *
 * Similar to KB completeness ring
 */
export function ConfidenceRing({
  confidence,
  dataQuality,
  size = 60,
  showLabel = false,
  className = '',
}: BaseConfidenceProps & { size?: number }) {
  const percent = confidence ?? (dataQuality ? dataQualityToPercent(dataQuality) : 0);
  const level = getConfidenceLevel(percent);

  const strokeWidth = size < 60 ? 4 : 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div
      className={`relative flex flex-col items-center ${className}`}
      title={`${level.label}: ${level.description}`}
    >
      <svg
        className="transform -rotate-90"
        width={size}
        height={size}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={level.ringColor}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className={`font-semibold ${size < 60 ? 'text-xs' : 'text-sm'} ${level.color}`}
        >
          {Math.round(percent)}%
        </span>
      </div>
      {showLabel && (
        <span className={`mt-1 text-xs font-medium ${level.color}`}>
          {level.label}
        </span>
      )}
    </div>
  );
}

/**
 * ConfidenceDot - Minimal color dot indicator
 *
 * For space-constrained contexts (optional percentage on hover)
 */
export function ConfidenceDot({
  confidence,
  dataQuality,
  showPercent = false,
  className = '',
}: BaseConfidenceProps) {
  const percent = confidence ?? (dataQuality ? dataQualityToPercent(dataQuality) : 0);
  const level = getConfidenceLevel(percent);

  return (
    <span
      className={`inline-flex items-center gap-1 ${className}`}
      title={`${Math.round(percent)}% - ${level.label}: ${level.description}`}
    >
      <span className={`w-2 h-2 rounded-full ${level.barColor}`} />
      {showPercent && (
        <span className={`text-xs ${level.color}`}>{Math.round(percent)}%</span>
      )}
    </span>
  );
}

/**
 * ConfidenceTooltip - Detailed tooltip content
 *
 * Use with a tooltip library for full details
 */
export function ConfidenceTooltip({
  confidence,
  dataQuality,
  sourceCount,
}: BaseConfidenceProps & { sourceCount?: number }) {
  const percent = confidence ?? (dataQuality ? dataQualityToPercent(dataQuality) : 0);
  const level = getConfidenceLevel(percent);

  return (
    <div className="p-3 max-w-xs">
      <div className="flex items-center gap-2 mb-2">
        <ConfidenceRing confidence={percent} size={40} />
        <div>
          <div className={`font-semibold ${level.color}`}>{level.label}</div>
          <div className="text-xs text-gray-500">{Math.round(percent)}% confidence</div>
        </div>
      </div>
      <p className="text-xs text-gray-600">{level.description}</p>
      {sourceCount !== undefined && (
        <p className="text-xs text-gray-500 mt-1">
          Based on {sourceCount} source{sourceCount !== 1 ? 's' : ''}
        </p>
      )}
    </div>
  );
}

// Default export for convenience
export default ConfidenceBadge;
